import { OfficeButton, OfficeHeader } from 'components';
import { useMatch } from 'react-location';
import { useCart } from './hooks/use-cart';
import moment from 'moment';
import { currencyCode, OrderStatus } from 'utils/lib';
import { useMutation } from '@apollo/client';
import { changeSupplierStatus } from '_graphql/mutation/order';
import { useState } from 'react';
import toast from 'react-hot-toast';
import OrderInvoice from './order-invoice';
import { useUrlState } from 'utils';
import SectionLayout from 'pages/create-product/components/SectionLayout';
import {
  ChangeSupplierStatus,
  ChangeSupplierStatusVariables,
} from '_graphql/mutation/__generated__/ChangeSupplierStatus';
import { useCurrentUser } from '_graphql/cache/auth';
import numeral from 'numeral';

const ViewOrders = () => {
  const { params } = useMatch();
  const currentUser = useCurrentUser();
  const [modal, setModal] = useUrlState('modal');
  const [status, setStatus] = useState('');
  const { cart } = useCart({
    filter: {
      id: {
        eq: params?.orderId,
      },
    },
  });

  const [process, { loading: loadingStatusUpdate }] = useMutation<
    ChangeSupplierStatus,
    ChangeSupplierStatusVariables
  >(changeSupplierStatus, {
    refetchQueries: ['GetCart'],
  });

  const changeStatus = (id: string) => {
    process({
      variables: {
        supplierStatus: status,
        changeSupplierStatusId: id,
      },
    })
      .then(({ data }) => {
        if (data?.changeSupplierStatus) {
          toast.success('Order Status Updated');
        }
      })
      .catch((err) => {
        toast.error(err?.message);
      });
  };

  return (
    <main className="flex-1 flex flex-col overflow-hidden h-screen bg-gray- mb-10">
      <OfficeHeader />
      <div className="grid lg:grid-cols-5 gap-2 overflow-y-scroll">
        <div className="lg:col-span-3 grid ">
          <SectionLayout freeFlow title="Order Items">
            <div className="grid gap-6 ">
              {cart?.orderItems
                ?.filter(
                  (order) => order?.product?.supplier_id === currentUser?.id
                )
                .map((order) => (
                  <div
                    key={order?.id}
                    className="pr-5 border-b border-gray-200 pb-5"
                  >
                    <div className="flex flex-row items-center space-x-3">
                      <img
                        src={order?.product?.avatar}
                        className="h-36 w-36 border border-gray-200 rounded-md object-cover"
                        alt=""
                      />
                      <div className="w-full flex flex-col space-y-2">
                        <span className="font-medium text-sm">
                          {order?.product?.title}
                        </span>
                        <div className="flex justify-between w-full">
                          <span className="text-sm text-gray-500">Price</span>
                          <span className="text-sm">
                            {currencyCode(
                              order?.product?.country ??
                                currentUser?.country ??
                                'GH'
                            )}{' '}
                            {numeral(order?.product?.cost_price ?? 0).format(
                              '0,0.00'
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between w-full">
                          <span className="text-sm text-gray-500">
                            Quantity
                          </span>
                          <span className="text-sm">{order?.quantity}</span>
                        </div>

                        <div className="flex items-center justify-between gap-1">
                          <span className="text-gray-500 text-sm">
                            Ops Status
                          </span>
                          <span className="font-medium text-xs text-gray-700 px-2 py-1 bg-slate-100 rounded-full">
                            {order?.status?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex flex-col gap-3 w-full border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0 w-full sm:flex-1 sm:pr-4">
                            <span className="text-gray-500 text-sm">
                              Supplier status
                            </span>
                          </div>
                          <div className="flex shrink-0 gap-2 self-end sm:self-auto">
                            <select
                              defaultValue={order?.supplier_status?.toString()}
                              onChange={(e) => setStatus(e.target.value)}
                              className="border-gray-400 rounded-md"
                              name=""
                              id=""
                            >
                              {OrderStatus.map((status) => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                            </select>
                            <OfficeButton
                              onClick={() => changeStatus(order?.id as string)}
                              variant="primary"
                            >
                              {loadingStatusUpdate ? 'Saving...' : 'Save'}
                            </OfficeButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </SectionLayout>

          <SectionLayout freeFlow title="Customer Details">
            <div className="grid md:grid-cols-3 gap-2">
              <div className="grid">
                <span className="text-gray-500 text-sm">Name</span>
                <span className="text-sm">
                  {cart?.customer?.name ?? 'Not Specified'}
                </span>
              </div>

              <div className="grid">
                <span className="text-gray-500 text-sm">Delivery Location</span>
                <span className="text-sm">
                  {cart?.address?.area ?? 'Not Specified'}
                </span>
              </div>
              <div className="grid">
                <span className="text-gray-500 text-sm">Delivery Price</span>
                <span className="text-sm">
                  {cart?.delivery?.price ?? 'Not Specified'}
                </span>
              </div>
            </div>
          </SectionLayout>
          <SectionLayout freeFlow title="Payment Details">
            <div className="grid md:grid-cols-3 gap-2">
              <div className="grid">
                <span className="text-gray-500 text-sm">Payment Method</span>
                {/* <span className="text-sm">{cart?.payment?.network || "Not Specified"}</span> */}
              </div>
              <div className="grid">
                <span className="text-gray-500 text-sm">Payment Status</span>
                <span className="text-sm">
                  {cart?.payment_status ?? 'Not Specified'}
                </span>
              </div>
              <div className="grid">
                <span className="text-gray-500 text-sm">Payment Reference</span>
                {/* <span className="text-sm">{cart?.payment?.reference || "Not Specified"}</span> */}
              </div>
            </div>
          </SectionLayout>
        </div>
        <div className="lg:col-span-2 flex flex-col">
          <SectionLayout
            freeFlow
            title="Order Details"
            renderActions={() => (
              <div className="">
                <OfficeButton
                  size="sm"
                  onClick={() => setModal('view')}
                  variant="tertiary"
                >
                  <span className="text-sm whitespace-nowrap">
                    View Invoice
                  </span>
                </OfficeButton>
              </div>
            )}
          >
            <div className="grid gap-2">
              <div className=" flex justify-between">
                <span className="text-sm text-gray-500 font-light">
                  Cart Number
                </span>
                <span className="text-sm">{cart?.cart_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 font-light">
                  Payment Status
                </span>
                <span className="text-sm">{cart?.payment_status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 font-light">
                  Tracking Code
                </span>
                <span className="text-sm">{cart?.tracking_code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 font-light">
                  Date Placed
                </span>
                <span className="text-sm">
                  {moment(cart?.createdAt).format('lll')}
                </span>
              </div>
            </div>
          </SectionLayout>
        </div>
      </div>
      <OrderInvoice
        supplierId={currentUser?.id as string}
        open={modal === 'view'}
        setOpen={(val: boolean) => setModal(val ? 'view' : undefined)}
        order={cart}
      />
    </main>
  );
};

export default ViewOrders;
